import React, { useEffect, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import Card from "components/card";
import {
  questionnaireService,
  Questionnaire,
  QuestionnaireAssignment,
} from "services/questionnaireService";
import {
  MdAssignment,
  MdCheckCircle,
  MdAccessTime,
  MdArrowForward,
  MdPlayArrow,
} from "react-icons/md";

// Update the Questionnaire interface to include the is_completed flag
interface EnhancedQuestionnaire extends Questionnaire {
  is_completed: boolean;
  detail_id: number;
}

const QuestionnaireLanding: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get("token");

  console.log("QuestionnaireLanding component rendered, token:", token);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [completed, setCompleted] = useState<boolean>(false);
  const [questionnaires, setQuestionnaires] = useState<EnhancedQuestionnaire[]>(
    []
  );
  const [assignment, setAssignment] = useState<QuestionnaireAssignment | null>(
    null
  );

  useEffect(() => {
    const fetchQuestionnaires = async () => {
      if (!token) {
        console.log("No token provided");
        setError("No se ha proporcionado un token válido");
        setLoading(false);
        return;
      }

      try {
        console.log("Fetching questionnaires with token:", token);
        setLoading(true);
        const response = await questionnaireService.getQuestionnairesByToken(
          token
        );
        console.log("API response:", response);

        if (response.success) {
          console.log(
            "Questionnaires loaded successfully:",
            response.data.questionnaires.length
          );
          // Cast the questionnaires to the enhanced type that includes is_completed
          setQuestionnaires(
            response.data.questionnaires as EnhancedQuestionnaire[]
          );
          setAssignment(response.data.assignment);
        } else {
          console.error("API returned error:", response.message);
          setError(response.message);
        }
      } catch (err) {
        console.error("Error fetching questionnaires:", err);
        setError(
          "Error al cargar los cuestionarios. Por favor, intente nuevamente más tarde."
        );
      } finally {
        setLoading(false);
      }
    };

    fetchQuestionnaires();
  }, [token]);

  const handleStartQuestionnaire = (
    questionnaireId: number,
    detailId: number
  ) => {
    console.log(
      `Starting questionnaire: ${questionnaireId}, detail ID: ${detailId}`
    );
    // Navigate to the questionnaire response page
    navigate(
      `/questionnaires/respond?token=${token}&questionnaire=${questionnaireId}&detail=${detailId}`
    );
  };

  if (loading) {
    return (
      <div className="flex h-screen items-center justify-center bg-gray-100">
        <div className="text-center">
          <div className="mx-auto mb-4 h-12 w-12 animate-spin rounded-full border-4 border-gray-200 border-t-brand-500"></div>
          <p className="text-lg font-semibold text-gray-700">
            Cargando cuestionarios...
          </p>
        </div>
      </div>
    );
  }

  if (error || !assignment) {
    return (
      <div className="flex h-screen items-center justify-center bg-gray-100 px-4">
        <div className="w-full max-w-md rounded-lg bg-white p-6 shadow-lg">
          <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-red-100">
            <svg
              className="h-8 w-8 text-red-500"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </div>
          <h2 className="mb-4 text-center text-xl font-bold text-gray-800 md:text-2xl">
            Error
          </h2>
          <p className="text-center text-sm text-gray-600 md:text-base">
            {error ||
              "No se ha podido cargar la información de los cuestionarios"}
          </p>
          <p className="mt-4 text-center text-xs text-gray-500 md:text-sm">
            Si el problema persiste, por favor contacte a su profesional de
            salud.
          </p>
        </div>
      </div>
    );
  }

  if (completed) {
    return <div>Cuestionarios completados</div>;
  }

  return (
    <div className="min-h-screen bg-gray-100 py-6">
      <div className="container mx-auto max-w-4xl px-4">
        <div className="mb-6 text-center">
          <h1 className="mb-2 text-2xl font-bold text-gray-800 md:text-3xl">
            Cuestionarios Asignados
          </h1>
          <p className="text-base text-gray-600 md:text-lg">
            Hola,{" "}
            <span className="font-semibold">{assignment.patient.name}</span>.
            <br className="md:hidden" /> Por favor, complete los siguientes
            cuestionarios.
          </p>
        </div>

        <Card extra="w-full p-4 md:p-6 mb-6">
          <div className="mb-4 flex flex-col md:flex-row md:items-center md:justify-between">
            <h2 className="mb-2 text-lg font-bold text-navy-700 dark:text-white md:mb-0 md:text-xl">
              Información
            </h2>
            <div className="flex items-center gap-2">
              <span className="text-sm font-medium text-gray-600">Estado:</span>
              {assignment.is_completed ? (
                <span className="flex items-center gap-1 text-green-500">
                  <MdCheckCircle className="h-5 w-5" />
                  <span>Completado</span>
                </span>
              ) : (
                <span className="flex items-center gap-1 text-amber-500">
                  <MdAccessTime className="h-5 w-5" />
                  <span>Pendiente</span>
                </span>
              )}
            </div>
          </div>

          <div className="mb-5 rounded-lg bg-gray-50 p-3 dark:bg-navy-800 md:p-4">
            <p className="text-sm text-gray-700 dark:text-gray-300 md:text-base">
              Estos cuestionarios han sido asignados por su profesional de
              salud. Sus respuestas son confidenciales y ayudarán a su
              profesional a brindarle una mejor atención.
            </p>
            {assignment.expires_at && (
              <p className="mt-2 text-xs text-amber-600 md:text-sm">
                <strong>Importante:</strong> Estos cuestionarios expiran el{" "}
                {new Date(assignment.expires_at).toLocaleDateString()}.
              </p>
            )}
          </div>

          <div className="mb-5">
            <h3 className="mb-3 text-base font-semibold text-gray-700 md:text-lg">
              Cuestionarios a completar ({questionnaires.length})
            </h3>
            <div className="space-y-3">
              {questionnaires.map((questionnaire) => (
                <div
                  key={questionnaire.id}
                  className="flex flex-col items-start rounded-lg border border-gray-200 p-3 md:flex-row md:p-4"
                >
                  <div className="mr-4 hidden h-10 w-10 flex-shrink-0 items-center justify-center rounded-full bg-brand-50 text-brand-500 md:flex">
                    <MdAssignment className="h-6 w-6" />
                  </div>
                  <div className="w-full flex-1">
                    <div className="flex flex-col justify-between md:flex-row md:items-start">
                      <div className="mb-3 md:mb-0">
                        <div className="mb-1 flex items-center md:mb-0">
                          <div className="mr-2 flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full bg-brand-50 text-brand-500 md:hidden">
                            <MdAssignment className="h-5 w-5" />
                          </div>
                          <h4 className="text-base font-medium text-gray-800 md:text-lg">
                            {questionnaire.name}
                          </h4>
                        </div>
                        <p className="mt-1 text-xs text-gray-600 md:text-sm">
                          {questionnaire.description}
                        </p>
                        <div className="mt-2 flex flex-wrap items-center gap-2">
                          <span className="rounded-full bg-gray-200 px-2 py-0.5 text-xs font-medium text-gray-700">
                            {questionnaire.code}
                          </span>
                          <span className="text-xs text-gray-500">
                            {questionnaire.structure.sections.length} secciones
                          </span>
                          <span className="text-xs text-gray-500">
                            {questionnaire.structure.sections.reduce(
                              (total, section) =>
                                total + section.questions.length,
                              0
                            )}{" "}
                            preguntas
                          </span>
                        </div>
                      </div>
                      <div className="mt-2 w-full md:ml-3 md:mt-0 md:w-auto">
                        {questionnaire.is_completed ? (
                          <div className="flex items-center justify-center gap-1 rounded-lg bg-green-100 px-3 py-2 text-green-700 md:justify-start">
                            <MdCheckCircle className="h-5 w-5" />
                            <span className="font-medium">Completado</span>
                          </div>
                        ) : (
                          <button
                            onClick={() =>
                              handleStartQuestionnaire(
                                questionnaire.id,
                                questionnaire.detail_id
                              )
                            }
                            className="flex w-full items-center justify-center gap-1 rounded-lg bg-brand-500 px-4 py-2 text-white transition-all hover:bg-brand-600 md:w-auto"
                          >
                            <MdPlayArrow className="h-5 w-5" />
                            <span className="font-medium">Comenzar</span>
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </Card>

        <div className="px-4 text-center text-xs text-gray-500 md:text-sm">
          <p>
            Si tiene alguna pregunta o problema, por favor contacte a su
            profesional de salud.
          </p>
        </div>
      </div>
    </div>
  );
};

export default QuestionnaireLanding;
